/**
 * widgets/app-shell/ui/AppShell — Authenticated application layout.
 *
 * Renders the full app chrome: header (branding, user menu, theme toggle,
 * language switcher) + sidebar (registry-driven nav) + main content area.
 *
 * Navigation items come exclusively from the module registry — no hardcoded
 * links. This widget knows nothing about individual features or pages.
 *
 * FSD: widgets may import from features, entities, and shared only.
 * Theme props are passed in from the app layer to avoid upward imports.
 */

import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Menu } from 'lucide-react';
import { AppNav } from '@/widgets/app-nav';
import { AppFooter } from '@/widgets/app-footer';
import { LanguageSwitcher } from '@/widgets/language-switcher';
import { NotificationCenter } from '@/widgets/notification-center';
import { OrgSwitcher } from '@/widgets/org-switcher';
import { Button } from '@/shared/ui/button';
import { ThemeToggle } from '@/shared/ui/theme-toggle';
import type { Theme } from '@/shared/lib/theme';
import { UserMenu } from './UserMenu';
import { MobileNavDrawer } from './MobileNavDrawer';

interface AppShellProps {
  children: ReactNode;
  /** Called after logout — parent (router) handles redirect to login. */
  onLogout: () => void;
  /** Called when user clicks Settings — parent (router) handles navigation. */
  onSettingsClick?: () => void;
  /** Called when user navigates to the notifications page. */
  onNotificationsClick?: () => void;
  /** Current theme value — passed from app layer which owns useTheme. */
  theme: Theme;
  /** Theme setter — passed from app layer which owns useTheme. */
  onThemeChange: (theme: Theme) => void;
}

export function AppShell({ children, onLogout, onSettingsClick, onNotificationsClick, theme, onThemeChange }: AppShellProps) {
  const { t } = useTranslation('common');
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  return (
    <div
      data-testid="app-shell"
      className="flex min-h-screen flex-col bg-background text-foreground"
    >
      {/* Skip link — first focusable element in the shell. Visually hidden
          until it receives keyboard focus, then jumps straight to <main>. */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:left-4 focus:top-4 focus:z-50 focus:rounded-md focus:bg-background focus:px-4 focus:py-2 focus:text-sm focus:font-medium focus:text-foreground focus:outline-none focus:ring-2 focus:ring-ring"
      >
        {t('shell.skipToContent')}
      </a>

      {/* ------------------------------------------------------------------ */}
      {/* Header */}
      {/* ------------------------------------------------------------------ */}
      <header className="flex h-14 shrink-0 items-center gap-2 border-b px-4 md:gap-4">
        {/* Mobile nav toggle — opens the drawer below md; hidden at md+ where
            the sidebar is always visible. */}
        <Button
          variant="ghost"
          size="icon"
          className="md:hidden"
          aria-label={t('shell.nav.openMenu')}
          data-testid="mobile-nav-toggle"
          onClick={() => setMobileNavOpen(true)}
        >
          <Menu className="h-5 w-5" aria-hidden="true" />
        </Button>

        {/* Branding */}
        <span
          className="min-w-0 truncate select-none text-base font-bold"
          aria-label={t('app.name')}
        >
          {t('app.name')}
        </span>

        {/* Spacer */}
        <div className="flex-1" />

        {/* Right-side controls */}
        <OrgSwitcher />
        {/* Language + theme controls: hidden below md (phone users switch
            these from the UserMenu dropdown instead — see UserMenu.tsx). */}
        <div className="hidden items-center gap-2 md:flex">
          <LanguageSwitcher />
          <ThemeToggle theme={theme} onThemeChange={onThemeChange} />
        </div>
        <NotificationCenter onViewAll={onNotificationsClick ?? (() => {})} />
        <UserMenu
          onLogout={onLogout}
          onSettingsClick={onSettingsClick}
          theme={theme}
          onThemeChange={onThemeChange}
        />
      </header>

      {/* ------------------------------------------------------------------ */}
      {/* Body: sidebar + main */}
      {/* ------------------------------------------------------------------ */}
      <div className="flex flex-1 overflow-hidden">
        {/* Sidebar — visible at md+ only. AppNav renders the single <nav>
            landmark (aria-label wired inside AppNav itself), so this <aside>
            is a plain layout container without its own redundant role. */}
        <aside className="hidden w-56 shrink-0 overflow-y-auto border-r py-4 md:block">
          <AppNav />
        </aside>

        {/* Mobile drawer — mounted regardless of breakpoint; Radix Dialog only
            renders its content while `open` is true, so it never duplicates
            the desktop sidebar's <nav> landmark. */}
        <MobileNavDrawer open={mobileNavOpen} onOpenChange={setMobileNavOpen} />

        {/* Main content column */}
        <div className="flex flex-1 flex-col overflow-y-auto">
          <main id="main-content" className="flex-1 p-4 md:p-6">
            {children}
          </main>
          <AppFooter />
        </div>
      </div>
    </div>
  );
}
